// yard/Squirrel.jsx - Animated squirrel that triggers chase events
import React, { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import useYardStore from './yardStore';

const Squirrel = () => {
  const groupRef = useRef();
  const bodyRef = useRef();
  const tailRef = useRef();
  const legFLRef = useRef();
  const legFRRef = useRef();
  const legBLRef = useRef();
  const legBRRef = useRef();

  const squirrelEvent = useYardStore(state => state.squirrelEvent);

  // Animation time
  const animTime = useRef(0);

  // Squirrel color
  const furColor = useMemo(() => new THREE.Color('#8B4513'), []);
  const bellyColor = useMemo(() => new THREE.Color('#D2B48C'), []);

  // Trail particles (simple dust effect)
  const trailRef = useRef([]);

  useFrame((state, delta) => {
    if (!groupRef.current || !squirrelEvent.active) return;

    animTime.current += delta;
    const t = animTime.current;

    // Position squirrel
    groupRef.current.position.set(
      squirrelEvent.position.x,
      squirrelEvent.position.y,
      squirrelEvent.position.z
    );

    // Face direction of travel
    groupRef.current.rotation.y = Math.PI / 2; // Facing right (positive X)

    // Running animation
    const runSpeed = 20;

    // Body bounce
    if (bodyRef.current) {
      bodyRef.current.position.y = 0.2 + Math.abs(Math.sin(t * runSpeed)) * 0.1;
    }

    // Tail wave
    if (tailRef.current) {
      tailRef.current.rotation.x = -0.5 + Math.sin(t * runSpeed * 0.5) * 0.3;
      tailRef.current.rotation.z = Math.sin(t * 8) * 0.2;
    }

    // Legs animation
    const legAmplitude = 0.6;
    if (legFLRef.current) legFLRef.current.rotation.x = Math.sin(t * runSpeed) * legAmplitude;
    if (legBRRef.current) legBRRef.current.rotation.x = Math.sin(t * runSpeed) * legAmplitude;
    if (legFRRef.current) legFRRef.current.rotation.x = Math.sin(t * runSpeed + Math.PI) * legAmplitude;
    if (legBLRef.current) legBLRef.current.rotation.x = Math.sin(t * runSpeed + Math.PI) * legAmplitude;
  });

  // Don't render if not active
  if (!squirrelEvent.active) return null;

  return (
    <group ref={groupRef} scale={0.6}>
      {/* Body */}
      <mesh ref={bodyRef} position={[0, 0.2, 0]} castShadow>
        <capsuleGeometry args={[0.15, 0.3, 4, 8]} rotation={[Math.PI / 2, 0, 0]} />
        <meshStandardMaterial color={furColor} roughness={0.9} />
      </mesh>

      {/* Belly (lighter fur) */}
      <mesh position={[0, 0.15, 0.08]} castShadow>
        <sphereGeometry args={[0.12, 8, 8]} />
        <meshStandardMaterial color={bellyColor} roughness={0.9} />
      </mesh>

      {/* Head */}
      <group position={[0, 0.3, 0.25]}>
        {/* Skull */}
        <mesh castShadow>
          <sphereGeometry args={[0.12, 8, 8]} />
          <meshStandardMaterial color={furColor} roughness={0.9} />
        </mesh>

        {/* Snout */}
        <mesh position={[0, -0.02, 0.1]} castShadow>
          <sphereGeometry args={[0.06, 8, 8]} />
          <meshStandardMaterial color={furColor} roughness={0.9} />
        </mesh>

        {/* Nose */}
        <mesh position={[0, 0, 0.16]}>
          <sphereGeometry args={[0.025, 8, 8]} />
          <meshStandardMaterial color="#1a1a1a" roughness={0.3} />
        </mesh>

        {/* Eyes */}
        <mesh position={[0.05, 0.03, 0.08]}>
          <sphereGeometry args={[0.03, 8, 8]} />
          <meshStandardMaterial color="#1a1a1a" roughness={0.3} />
        </mesh>
        <mesh position={[-0.05, 0.03, 0.08]}>
          <sphereGeometry args={[0.03, 8, 8]} />
          <meshStandardMaterial color="#1a1a1a" roughness={0.3} />
        </mesh>

        {/* Ears */}
        <mesh position={[0.08, 0.1, 0]} castShadow>
          <sphereGeometry args={[0.04, 8, 8]} />
          <meshStandardMaterial color={furColor} roughness={0.9} />
        </mesh>
        <mesh position={[-0.08, 0.1, 0]} castShadow>
          <sphereGeometry args={[0.04, 8, 8]} />
          <meshStandardMaterial color={furColor} roughness={0.9} />
        </mesh>

        {/* Cheeks (fluffy) */}
        <mesh position={[0.1, -0.02, 0.02]} castShadow>
          <sphereGeometry args={[0.05, 8, 8]} />
          <meshStandardMaterial color={bellyColor} roughness={0.9} />
        </mesh>
        <mesh position={[-0.1, -0.02, 0.02]} castShadow>
          <sphereGeometry args={[0.05, 8, 8]} />
          <meshStandardMaterial color={bellyColor} roughness={0.9} />
        </mesh>
      </group>

      {/* Tail (big fluffy squirrel tail) */}
      <group ref={tailRef} position={[0, 0.25, -0.25]}>
        <mesh position={[0, 0.15, -0.1]} rotation={[-0.5, 0, 0]} castShadow>
          <capsuleGeometry args={[0.08, 0.25, 4, 8]} />
          <meshStandardMaterial color={furColor} roughness={0.9} />
        </mesh>
        <mesh position={[0, 0.35, -0.15]} rotation={[-0.8, 0, 0]} castShadow>
          <capsuleGeometry args={[0.1, 0.2, 4, 8]} />
          <meshStandardMaterial color={furColor} roughness={0.9} />
        </mesh>
        <mesh position={[0, 0.5, -0.12]} rotation={[-1.2, 0, 0]} castShadow>
          <sphereGeometry args={[0.12, 8, 8]} />
          <meshStandardMaterial color={furColor} roughness={0.9} />
        </mesh>
      </group>

      {/* Legs */}
      {/* Front Left */}
      <group ref={legFLRef} position={[0.08, 0.1, 0.15]}>
        <mesh position={[0, -0.08, 0]} castShadow>
          <capsuleGeometry args={[0.025, 0.1, 4, 8]} />
          <meshStandardMaterial color={furColor} roughness={0.9} />
        </mesh>
      </group>

      {/* Front Right */}
      <group ref={legFRRef} position={[-0.08, 0.1, 0.15]}>
        <mesh position={[0, -0.08, 0]} castShadow>
          <capsuleGeometry args={[0.025, 0.1, 4, 8]} />
          <meshStandardMaterial color={furColor} roughness={0.9} />
        </mesh>
      </group>

      {/* Back Left */}
      <group ref={legBLRef} position={[0.08, 0.1, -0.1]}>
        <mesh position={[0, -0.1, 0]} castShadow>
          <capsuleGeometry args={[0.03, 0.12, 4, 8]} />
          <meshStandardMaterial color={furColor} roughness={0.9} />
        </mesh>
      </group>

      {/* Back Right */}
      <group ref={legBRRef} position={[-0.08, 0.1, -0.1]}>
        <mesh position={[0, -0.1, 0]} castShadow>
          <capsuleGeometry args={[0.03, 0.12, 4, 8]} />
          <meshStandardMaterial color={furColor} roughness={0.9} />
        </mesh>
      </group>

      {/* Exclamation mark above squirrel */}
      <group position={[0, 0.8, 0]}>
        <mesh>
          <cylinderGeometry args={[0.03, 0.03, 0.15, 8]} />
          <meshBasicMaterial color="#ffcc00" />
        </mesh>
        <mesh position={[0, -0.12, 0]}>
          <sphereGeometry args={[0.03, 8, 8]} />
          <meshBasicMaterial color="#ffcc00" />
        </mesh>
      </group>

      {/* Motion blur / speed lines (simple) */}
      {[0.1, 0.2, 0.3].map((offset, i) => (
        <mesh
          key={i}
          position={[-0.3 - offset, 0.2, Math.sin(i) * 0.1]}
          scale={[0.2 - i * 0.05, 0.02, 0.02]}
        >
          <boxGeometry />
          <meshBasicMaterial color="#ffffff" transparent opacity={0.3 - i * 0.1} />
        </mesh>
      ))}
    </group>
  );
};

export default Squirrel;
